import { useMemo } from 'react';
import { Users } from 'lucide-react';
import useLocalState from '../hooks/useLocalState.js';
import { computeRank, TOTAL_ADDRESSES, DISTRIBUTION_DATE } from '../lib/distribution.js';
import { formatBtc } from '../lib/format.js';

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

export default function OnchainRank() {
  const [stack] = useLocalState('myStack', { btc: 0 }, 1);
  const rank = useMemo(() => computeRank(stack.btc), [stack.btc]);

  return (
    <CardShell>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
          <Users size={20} className="text-stone-900" />
        </div>
        <h3 className="font-display text-lg font-semibold text-cream">On-chain Rank</h3>
      </div>

      {!rank ? (
        <div className="text-sm text-stone-500">
          Enter your holdings in "My Stack" to see where you rank among on-chain BTC addresses.
        </div>
      ) : (
        <>
          {/* Headline percentile */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              Your stack of {formatBtc(stack.btc)} puts you in
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-mono tabular-nums text-3xl font-bold text-amber-400">
                top {rank.percentile < 1 ? rank.percentile.toFixed(2) : rank.percentile.toFixed(1)}%
              </span>
            </div>
            <div className="text-xs text-stone-500 mt-1">
              of all {(TOTAL_ADDRESSES / 1e6).toFixed(1)}M non-zero BTC addresses
            </div>
          </div>

          {/* Context facts */}
          <div className="space-y-2 mb-3">
            <RankFact
              label="Addresses holding more than you"
              value={rank.addressesAbove.toLocaleString()}
            />
            <RankFact
              label="Addresses holding less than you"
              value={(TOTAL_ADDRESSES - rank.addressesAbove - 1).toLocaleString()}
            />
          </div>

          {/* Milestone nudges */}
          <MilestoneNudge currentBtc={stack.btc} />

          {/* Data freshness */}
          <p className="text-[10px] text-stone-600 mt-3">
            Reference distribution as of {DISTRIBUTION_DATE}. On-chain address
            counts shift slowly (fractions of a percent per month). One address
            ≠ one person — many people use multiple addresses, and exchanges hold
            for millions of users in single addresses.
          </p>
        </>
      )}
    </CardShell>
  );
}

function RankFact({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-stone-950/40 px-3 py-2">
      <span className="text-xs text-stone-500">{label}</span>
      <span className="font-mono tabular-nums text-xs text-stone-300">{value}</span>
    </div>
  );
}

// Show the next meaningful milestone the user could reach
function MilestoneNudge({ currentBtc }) {
  const milestones = [0.01, 0.1, 0.21, 1, 2.1, 10];
  const next = milestones.find(m => m > currentBtc);
  if (!next) return null;
  const nextRank = computeRank(next);
  if (!nextRank) return null;

  return (
    <div className="rounded-lg bg-amber-400/5 border border-amber-400/20 px-3 py-2 mt-3">
      <div className="text-[10px] uppercase tracking-wider text-amber-400/70 mb-0.5">
        Next milestone
      </div>
      <div className="text-xs text-stone-400">
        Reach <span className="text-cream font-mono">{formatBtc(next)}</span> to enter the{' '}
        <span className="text-amber-400 font-mono">
          top {nextRank.percentile < 1 ? nextRank.percentile.toFixed(2) : nextRank.percentile.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
